// @ts-check
import {
    getFeatureComponents,
    featureConversionRate
} from "@functions/services/billingUsage/billingUsageFeatureConversion";
import {
    getWorkspaceSettingsForPlan,
    isCustomEnterprisePlan,
    isSelfServePaidPlan,
    MONTHLY_PRICE_PER_CREDIT,
    ANNUAL_PRICE_PER_CREDIT,
    STANDARD_PRICE_PER_CREDIT_USD
} from "@functions/workspacePlans";
import { isCreditSystemEnabled } from "../routes/workspace/utils";

export const UBP_CREDIT_TYPES = {
    dataset: {
        id: "dataset",
        name: "Dataset",
        color: "yellow",
        graphColor: "#FCD34D",
        icon: "fa-solid fa-image"
    },
    training: {
        id: "training",
        name: "Training",
        color: "green",
        graphColor: "#6EE7B7",
        icon: "fa-solid fa-bolt"
    },
    deployment: {
        id: "deployment",
        name: "Deploy",
        color: "blue",
        graphColor: "#7DD3FC",
        icon: "fa-solid fa-rocket"
    }
};

export const UBP_FEATURE_UI_GROUPS = {
    storage: {
        id: "storage",
        name: "Storage",
        icon: "far fa-images",
        type: UBP_CREDIT_TYPES.dataset.id,
        description:
            "Total count of images stored on Roboflow. All labeled and unlabeled images in a workspace count toward this limit. Image storage credits are assessed hourly, ensuring you only pay for images actually stored.",
        unitMeasurementName: "images a month",
        unitMeasurementAlreadyPlural: true
    },
    indexing: {
        id: "indexing",
        name: "Uploads",
        icon: "far fa-arrow-up",
        type: UBP_CREDIT_TYPES.dataset.id,
        description:
            "Occurs once per upload. Roboflow indexes and applies embeddings to the images on upload to improve internal labeling and search functionality.",
        unitMeasurementName: "image"
    },
    augmentation: {
        id: "augmentation",
        name: "Versions",
        icon: "far fa-rectangle-vertical-history",
        type: UBP_CREDIT_TYPES.dataset.id,
        description:
            "When creating a version, you are charged once for the total number of images created in the version",
        unitMeasurementName: "image"
    },
    aiLabeling: {
        id: "aiLabeling",
        name: "AI Labeling",
        icon: "far fa-sparkles",
        type: UBP_CREDIT_TYPES.dataset.id,
        description:
            "Total images labeled with Roboflow's AI Labeling tools: Auto Label, Label Assist, Box Prompting, and Smart Select. ",
        unitMeasurementName: "image"
    },
    modelTraining: {
        id: "modelTraining",
        name: "Model Training",
        icon: "far fa-bolt",
        type: UBP_CREDIT_TYPES.training.id,
        description:
            "Length of time that the model trains for on Roboflow's platform. Only trains that are successfully completed or intentionally stopped early count towards this time.",
        conversionRateTransform: (c) => c * 60, // seconds to minutes
        unitMeasurementName: "minute"
    },
    serverlessInferenceRun: {
        id: "serverlessInferenceRun",
        name: "Hosted Inference V2",
        icon: "far fa-cloud-plus",
        type: UBP_CREDIT_TYPES.deployment.id,
        description:
            "Our Serverless Hosted API (v2) is more powerful than our legacy API, running inferences on GPU architecture with the ability to call VLMs and additional models. We calculate the sum total of seconds that the models are run for. Due to the nature of workflows executing multiple models in parallel, it is possible for the inference execution to exceed actual time passed.",
        unitMeasurementName: "second"
    },
    hostedInferenceImages: {
        id: "hostedInferenceImages",
        name: "Hosted Inference (Image)",
        icon: "far fa-cloud",
        type: UBP_CREDIT_TYPES.deployment.id,
        description:
            "<span>Each API call to our <a href='https://docs.roboflow.com/deploy/hosted-api' target='_blank' rel='noreferrer'>hosted inference endpoint</a> counts as one Inference, no matter how many classes (if any) were detected. Workflows on the Hosted Inference API will be billed per model where an inference is run (there are often multiple inference per Workflow).</span>",
        unitMeasurementName: "inference"
    },
    batchVideoInference: {
        id: "batchVideoInference",
        name: "Hosted Inference (Stored Video)",
        icon: "far fa-cloud",
        type: UBP_CREDIT_TYPES.deployment.id,
        description:
            "Videos submitted to the Hosted Video Inference API. Credits are consumed per minute of video FPS.",
        conversionRateTransform: (c) => c / 60, // hours to minutes
        unitMeasurementName: "minute"
    },
    batchProcessingCPU: {
        id: "batchProcessingCPU",
        name: "Batch Processing (CPU)",
        icon: "far fa-bulldozer",
        type: UBP_CREDIT_TYPES.deployment.id,
        description: "Length of time Batch Processing CPU machines are running.",
        conversionRateTransform: (c) => c * 60 * 60, // seconds to hours
        unitMeasurementName: "hour"
    },
    batchProcessingGPU: {
        id: "batchProcessingGPU",
        name: "Batch Processing (GPU)",
        icon: "far fa-bulldozer",
        type: UBP_CREDIT_TYPES.deployment.id,
        description: "Length of time Batch Processing GPU machines are running.",
        conversionRateTransform: (c) => c * 60 * 60, // seconds to hours
        unitMeasurementName: "hour"
    },
    dedicatedDeploymentCPU: {
        id: "dedicatedDeploymentCPU",
        name: "Dedicated Deployment (CPU)",
        icon: "far fa-server",
        type: UBP_CREDIT_TYPES.deployment.id,
        description: "Length of time CPUs are running.",
        unitMeasurementName: "hour"
    },
    dedicatedDeploymentGPU: {
        id: "dedicatedDeploymentGPU",
        name: "Dedicated Deployment (GPU)",
        icon: "far fa-server",
        type: UBP_CREDIT_TYPES.deployment.id,
        description: "Length of time GPUs are running.",
        unitMeasurementName: "hour"
    },
    selfHostedInferenceRun: {
        id: "selfHostedInferenceRun",
        name: "Self Hosted (Image)",
        icon: "far fa-microchip",
        type: UBP_CREDIT_TYPES.deployment.id,
        description:
            "Number of images inferred on using the <a href='https://inference.roboflow.com/quickstart/roboflow_ecosystem/' target='_blank' rel='noreferrer'>Self Hosted Inference SDKs.</a> (1 credit covers 3,000 inferences – equivalent of 48 hours of live video running at 1 frame per minute)",
        unitMeasurementName: "inference"
    },
    selfHostedStream: {
        id: "selfHostedStream",
        name: "Self Hosted (Video Stream)",
        icon: "far fa-microchip",
        type: UBP_CREDIT_TYPES.deployment.id,
        description:
            "Number of minutes of video stream processed using the <a href='https://inference.roboflow.com/quickstart/roboflow_ecosystem/' target='_blank' rel='noreferrer'>Self Hosted Inference SDKs, regardless of FPS. Counts as the quantity of video footage provided from a single camera. If a single camera processes more than 10,000 minutes of footage in a month, credit usage for that camera will be capped at 20.</a>",
        conversionRateTransform: (c) => c * 60, // seconds to minutes
        unitMeasurementName: "minute"
    },
    thirdPartyApi: {
        id: "thirdPartyApi",
        name: "Workflows (3rd Party Blocks)",
        icon: "far fa-plug",
        type: UBP_CREDIT_TYPES.deployment.id,
        description: "Workflow blocks that make 3rd party API requests without your own API Key",
        unitDisplayString:
            "<a href='https://roboflow.com/credits' target='_blank' rel='noreferrer'>credits vary by vendor</a>"
    },
    serverlessStreamingCpu: {
        id: "serverlessStreamingCpu",
        name: "Serverless Video Streaming (CPU)",
        icon: "far fa-video",
        type: UBP_CREDIT_TYPES.deployment.id,
        description:
            "Real-time video streaming for workflows using CPU. Credits are consumed per second of execution time.",
        unitMeasurementName: "hour",
        conversionRateTransform: (c) => c * 3600,
        unitDisplayString: "0.1 credits / hour"
    },
    serverlessStreamingGpuSmall: {
        id: "serverlessStreamingGpuSmall",
        name: "Serverless Video Streaming (GPU Small)",
        icon: "far fa-video",
        type: UBP_CREDIT_TYPES.deployment.id,
        description:
            "Real-time video streaming for workflows using GPU Small. Credits are consumed per second of execution time.",
        unitMeasurementName: "hour",
        conversionRateTransform: (c) => c * 3600,
        unitDisplayString: "0.75 credits / hour"
    },
    serverlessStreamingGpuMedium: {
        id: "serverlessStreamingGpuMedium",
        name: "Serverless Video Streaming (GPU Medium)",
        icon: "far fa-video",
        type: UBP_CREDIT_TYPES.deployment.id,
        description:
            "Real-time video streaming for workflows using GPU Medium. Credits are consumed per second of execution time.",
        unitMeasurementName: "hour",
        conversionRateTransform: (c) => c * 3600,
        unitDisplayString: "1 credit / hour"
    },
    serverlessStreamingGpuLarge: {
        id: "serverlessStreamingGpuLarge",
        name: "Serverless Video Streaming (GPU Large)",
        icon: "far fa-video",
        type: UBP_CREDIT_TYPES.deployment.id,
        description:
            "Real-time video streaming for workflows using GPU Large. Credits are consumed per second of execution time.",
        unitMeasurementName: "hour",
        conversionRateTransform: (c) => c * 3600,
        unitDisplayString: "2 credits / hour"
    }
};

export const UBP_FEATURES_PARAMETERIZATION = {
    "image-source-storage": {
        id: "image-source-storage",
        uiGroup: UBP_FEATURE_UI_GROUPS.storage.id
    },
    "image-transform-storage": {
        id: "image-transform-storage",
        uiGroup: UBP_FEATURE_UI_GROUPS.storage.id
    },
    "ai-labeling": { id: "ai-labeling", uiGroup: UBP_FEATURE_UI_GROUPS.aiLabeling.id },
    "image-indexing": { id: "image-indexing", uiGroup: UBP_FEATURE_UI_GROUPS.indexing.id },
    "image-generation": { id: "image-generation", uiGroup: UBP_FEATURE_UI_GROUPS.augmentation.id },
    "train": { id: "train", uiGroup: UBP_FEATURE_UI_GROUPS.modelTraining.id },
    "serverless-inference-run": {
        id: "serverless-inference-run",
        uiGroup: UBP_FEATURE_UI_GROUPS.serverlessInferenceRun.id
    },
    "hostedInference": {
        id: "hostedInference",
        uiGroup: UBP_FEATURE_UI_GROUPS.hostedInferenceImages.id
    },
    "batch-video-inference": {
        id: "batch-video-inference",
        uiGroup: UBP_FEATURE_UI_GROUPS.batchVideoInference.id
    },
    "dedicated-machine-hourly_dev-cpu": {
        id: "dedicated-machine-hourly_dev-cpu",
        uiGroup: UBP_FEATURE_UI_GROUPS.dedicatedDeploymentCPU.id
    },
    "dedicated-machine-hourly_dev-gpu": {
        id: "dedicated-machine-hourly_dev-gpu",
        uiGroup: UBP_FEATURE_UI_GROUPS.dedicatedDeploymentGPU.id
    },
    "dedicated-machine-hourly_prod-cpu": {
        id: "dedicated-machine-hourly_prod-cpu",
        uiGroup: UBP_FEATURE_UI_GROUPS.dedicatedDeploymentCPU.id
    },
    "dedicated-machine-hourly_prod-gpu": {
        id: "dedicated-machine-hourly_prod-gpu",
        uiGroup: UBP_FEATURE_UI_GROUPS.dedicatedDeploymentGPU.id
    },
    "self-hosted-stream": {
        id: "self-hosted-stream",
        uiGroup: UBP_FEATURE_UI_GROUPS.selfHostedStream.id
    },
    "self-hosted-inference-run": {
        id: "self-hosted-inference-run",
        uiGroup: UBP_FEATURE_UI_GROUPS.selfHostedInferenceRun.id
    },
    "batch-processing-compute-gpu": {
        id: "batch-processing-compute-gpu",
        uiGroup: UBP_FEATURE_UI_GROUPS.batchProcessingGPU.id
    },
    "batch-processing-compute-cpu": {
        id: "batch-processing-compute-cpu",
        uiGroup: UBP_FEATURE_UI_GROUPS.batchProcessingCPU.id
    },
    "webrtc-cpu": { id: "webrtc-cpu", uiGroup: UBP_FEATURE_UI_GROUPS.serverlessStreamingCpu.id },
    "webrtc-gpu-small": {
        id: "webrtc-gpu-small",
        uiGroup: UBP_FEATURE_UI_GROUPS.serverlessStreamingGpuSmall.id
    },
    "webrtc-gpu-medium": {
        id: "webrtc-gpu-medium",
        uiGroup: UBP_FEATURE_UI_GROUPS.serverlessStreamingGpuMedium.id
    },
    "webrtc-gpu-large": {
        id: "webrtc-gpu-large",
        uiGroup: UBP_FEATURE_UI_GROUPS.serverlessStreamingGpuLarge.id
    }
};

// Dynamically add OpenAI third-party API features
const openAiModelSlugs = [
    "gpt-4o",
    "gpt-4o-mini",
    "o3",
    "o4-mini",
    "gpt-4-1",
    "gpt-4-1-mini",
    "gpt-4-1-nano",
    "gpt-5",
    "gpt-5-mini",
    "gpt-5-nano",
    "gpt-5-1",
    "gpt-5-2"
];

for (const modelSlug of openAiModelSlugs) {
    UBP_FEATURES_PARAMETERIZATION[`third-party-api--open-ai-${modelSlug}-input`] = {
        id: `third-party-api--open-ai-${modelSlug}-input`,
        uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
    };
    UBP_FEATURES_PARAMETERIZATION[`third-party-api--open-ai-${modelSlug}-output`] = {
        id: `third-party-api--open-ai-${modelSlug}-output`,
        uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
    };
}

// Email notification API feature
UBP_FEATURES_PARAMETERIZATION["third-party-api--email-notification"] = {
    id: "third-party-api--email-notification",
    uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
};

// Twilio SMS API features (with country parameterization for analytics)
// Base features
UBP_FEATURES_PARAMETERIZATION["third-party-api--twilio-sms"] = {
    id: "third-party-api--twilio-sms",
    uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
};
// US/Canada SMS
UBP_FEATURES_PARAMETERIZATION["third-party-api--twilio-sms_us-ca"] = {
    id: "third-party-api--twilio-sms_us-ca",
    uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
};
// International SMS
UBP_FEATURES_PARAMETERIZATION["third-party-api--twilio-sms_intl"] = {
    id: "third-party-api--twilio-sms_intl",
    uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
};

// Twilio MMS API features (with country parameterization for analytics)
// Base features
UBP_FEATURES_PARAMETERIZATION["third-party-api--twilio-mms"] = {
    id: "third-party-api--twilio-mms",
    uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
};
// US/Canada MMS
UBP_FEATURES_PARAMETERIZATION["third-party-api--twilio-mms_us-ca"] = {
    id: "third-party-api--twilio-mms_us-ca",
    uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
};
// International MMS
UBP_FEATURES_PARAMETERIZATION["third-party-api--twilio-mms_intl"] = {
    id: "third-party-api--twilio-mms_intl",
    uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
};

// Google Vision OCR API feature
UBP_FEATURES_PARAMETERIZATION["third-party-api--google-vision-ocr"] = {
    id: "third-party-api--google-vision-ocr",
    uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
};

// Gemini API features (token-based, like OpenAI)
const geminiModelSlugs = [
    "gemini-3-pro-preview",
    "gemini-3-flash-preview",
    "gemini-2-5-pro",
    "gemini-2-5-flash",
    "gemini-2-5-flash-lite",
    "gemini-2-0-flash",
    "gemini-2-0-flash-lite"
];

for (const modelSlug of geminiModelSlugs) {
    UBP_FEATURES_PARAMETERIZATION[`third-party-api--gemini-${modelSlug}-input`] = {
        id: `third-party-api--gemini-${modelSlug}-input`,
        uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
    };
    UBP_FEATURES_PARAMETERIZATION[`third-party-api--gemini-${modelSlug}-output`] = {
        id: `third-party-api--gemini-${modelSlug}-output`,
        uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
    };
}

// Anthropic API features (token-based, like OpenAI/Gemini)
const anthropicModelSlugs = [
    "claude-opus-4-5",
    "claude-opus-4-1",
    "claude-opus-4",
    "claude-sonnet-4-5",
    "claude-sonnet-4",
    "claude-haiku-4-5"
];

for (const modelSlug of anthropicModelSlugs) {
    UBP_FEATURES_PARAMETERIZATION[`third-party-api--anthropic-${modelSlug}-input`] = {
        id: `third-party-api--anthropic-${modelSlug}-input`,
        uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
    };
    UBP_FEATURES_PARAMETERIZATION[`third-party-api--anthropic-${modelSlug}-output`] = {
        id: `third-party-api--anthropic-${modelSlug}-output`,
        uiGroup: UBP_FEATURE_UI_GROUPS.thirdPartyApi.id
    };
}

export function findFeatureParametersFromGroupId(groupId) {
    return Object.values(UBP_FEATURES_PARAMETERIZATION).filter(
        (feature) => feature.uiGroup === groupId
    )[0];
}

export function getFeatureParameters(featureId) {
    const featureComponents = getFeatureComponents(featureId);

    for (const featureComponent of featureComponents) {
        if (UBP_FEATURES_PARAMETERIZATION[featureComponent]) {
            return UBP_FEATURES_PARAMETERIZATION[featureComponent];
        }
    }

    return null;
}

export function getFeatureUIGroup(featureId) {
    return getFeatureParameters(featureId)?.uiGroup;
}

export function getFeatureCreditType(featureId) {
    const uiGroup = getFeatureUIGroup(featureId);

    if (!uiGroup) return null;

    return UBP_FEATURE_UI_GROUPS[uiGroup]?.type;
}

/**
 * @param {Object} param0
 * @param {Object} param0.usage
 * @returns {number | string} - Returns credit cap number, or "none" string if not set
 */
export function getCreditCap({ usage }) {
    if (!usage) {
        // eslint-disable-next-line no-console
        console.error("Usage is required");
        return 0;
    }

    const prepaidInCredits = getPrepaidInCredits({ usage });

    if (usage?.ubp?.creditCap === "none") return "none";

    if (typeof usage?.ubp?.creditCap === "number") {
        return usage.ubp.creditCap + prepaidInCredits;
    }

    if (isCustomEnterprisePlan(usage.plan)) {
        return "none";
    } else {
        const usageDefaults = getWorkspaceSettingsForPlan(usage.plan);
        if (typeof usageDefaults?.ubp?.creditCap === "number") {
            return usageDefaults?.ubp?.creditCap + prepaidInCredits;
        }
        return "none";
    }
}

export function isFlexBillingEnabled({ usage }) {
    const creditCap = getCreditCap({ usage });
    return creditCap === "none";
}

export function canToggleFlexBilling({ usage }) {
    return isCreditSystemEnabled(usage) && isSelfServePaidPlan(usage.plan);
}

/**
 * @param {Object} param0
 * @param {Object} param0.usage
 * @returns {number} - Returns included credits number
 */
export function getIncludedCredits({ usage }) {
    if (typeof usage?.ubp?.includedCredits === "number") {
        return usage?.ubp?.includedCredits;
    }

    const usageDefaults = getWorkspaceSettingsForPlan(usage?.plan);

    return usageDefaults?.ubp?.includedCredits ?? 0;
}

/**
 * @param {Object} param0
 * @param {Object} param0.usage
 * @returns {number} - Returns price per credit number
 */
export function getPricePerCredit({ usage }) {
    if (!usage) {
        // eslint-disable-next-line no-console
        console.error("Usage is required");
        return STANDARD_PRICE_PER_CREDIT_USD;
    }

    if (typeof usage.ubp?.pricePerCredit === "number") {
        return usage.ubp.pricePerCredit;
    }

    const usageDefaults = getWorkspaceSettingsForPlan(usage.plan);

    if (typeof usageDefaults?.ubp?.pricePerCredit === "number") {
        return usageDefaults.ubp.pricePerCredit;
    }

    return STANDARD_PRICE_PER_CREDIT_USD;
}

export const DEFAULT_BILLING_THRESHOLD_CENTS = 5000;

/**
 * @param {Object} param0
 * @param {Object} param0.usage
 * @returns {number | null} - Returns flex invoice threshold number
 */
export function getFlexInvoiceThresholdInCents({ usage }) {
    return usage?.ubp?.flexInvoiceThresholdInCents || null;
}

export function calculateOptimalBillingThresholdInCents({ amountPaidInCents }) {
    // Intended for initial flex invoice threshold creation after paid invoice
    let newThresholdInCents = DEFAULT_BILLING_THRESHOLD_CENTS;

    if (amountPaidInCents * 2 > DEFAULT_BILLING_THRESHOLD_CENTS) {
        let exponent = 1;
        while (Math.pow(2, exponent) * DEFAULT_BILLING_THRESHOLD_CENTS < amountPaidInCents * 2) {
            exponent++;
        }

        exponent--;

        newThresholdInCents = Math.pow(2, exponent) * DEFAULT_BILLING_THRESHOLD_CENTS;
    }

    return newThresholdInCents;
}

export function getMaxCreditsPerStream(args = {}) {
    const { usage } = args;
    if (typeof usage?.maxCreditsPerStream === "number") {
        return usage?.maxCreditsPerStream;
    }

    return 20;
}

export function getPrepaidInCents({ usage }) {
    if (typeof usage?.ubp?.prepaidInCents === "number") {
        return usage?.ubp?.prepaidInCents;
    }

    return 0;
}

export function getPrepaidInCredits({ usage }) {
    const prepaidInCents = getPrepaidInCents({ usage });
    const prepaidInDollars = prepaidInCents / 100;
    const pricePerCredit = getPricePerCredit({ usage });
    if (pricePerCredit === 0) return 0; // avoid division by zero returning NaN

    return prepaidInDollars / pricePerCredit;
}

export function getPrepaidAndIncludedCredits({ usage }) {
    return getIncludedCredits({ usage }) + getPrepaidInCredits({ usage });
}

export function readableFeatureConversionRate({ usage, feature }) {
    const conversionRate = featureConversionRate({ usage, feature });
    const { uiGroup } = getFeatureParameters(feature) || {};
    const {
        conversionRateTransform,
        unitMeasurementName = "",
        unitMeasurementAlreadyPlural,
        unitDisplayString
    } = UBP_FEATURE_UI_GROUPS[uiGroup] || {};
    const displayConversionRate = conversionRateTransform?.(conversionRate) || conversionRate;
    const displayConversionRatePerCredit = 1 / displayConversionRate;
    const pluralizedUnitMeasurementName =
        displayConversionRatePerCredit !== 1 && !unitMeasurementAlreadyPlural && unitMeasurementName
            ? unitMeasurementName + "s"
            : unitMeasurementName;
    const displayConversionRatePerCreditStr =
        displayConversionRatePerCredit?.toLocaleString() || "";
    return {
        conversionRate,
        displayConversionRatePerCredit,
        displayPerCredit: unitDisplayString
            ? unitDisplayString
            : `${displayConversionRatePerCreditStr} ${pluralizedUnitMeasurementName} / credit`,
        unitMeasurementName: pluralizedUnitMeasurementName
    };
}

export default {
    getFeatureCreditType,
    UBP_CREDIT_TYPES,
    UBP_FEATURE_UI_GROUPS,
    UBP_FEATURES_PARAMETERIZATION,
    getFeatureUIGroup,
    getCreditCap,
    getIncludedCredits,
    getPricePerCredit,
    getFlexInvoiceThresholdInCents,
    calculateOptimalBillingThresholdInCents,
    DEFAULT_BILLING_THRESHOLD_CENTS,
    getMaxCreditsPerStream,
    getPrepaidInCents,
    getPrepaidInCredits,
    readableFeatureConversionRate,
    findFeatureParametersFromGroupId,
    getPrepaidAndIncludedCredits,
    getFeatureParameters,
    isFlexBillingEnabled,
    canToggleFlexBilling
};
